import * as L from 'leaflet';

/**
 * Shared-corner relationships used by the territory boundary editor.
 *
 * Territories that touch share a border, and until now each kept its own copy
 * of it. Dragging a corner moved one copy: the patch being edited changed shape
 * and its neighbour did not, so the border came apart - a sliver of overlap on
 * one side of the drag, a sliver of nobody's land on the other. The overlap
 * half is repaired on save. The gap half never was, because nothing looks for
 * it: a hole between two territories is not invalid, it is just wrong.
 *
 * So a shared corner has to move in every shape that holds it, in one gesture.
 * These functions find which corners those are.
 */

/**
 * How close two corners must be to count as the same one.
 *
 * They normally ARE the same, to the last bit: snapping copies a neighbour's
 * coordinate rather than approximating it, and a round trip through PostGIS and
 * GeoJSON preserves the double. The tolerance covers boundaries drawn before
 * snapping existed, and is about a tenth of a millimetre on the ground - far
 * below anything a person could have meant to draw.
 */
export const SharedPointEpsilon = 1e-9;

export function samePoint(a: L.LatLng, b: L.LatLng): boolean {
  return Math.abs(a.lat - b.lat) <= SharedPointEpsilon
      && Math.abs(a.lng - b.lng) <= SharedPointEpsilon;
}

/** A shape being edited: a territory, or a draft of one. */
export interface EditableShape {
  /** Territory id. Negative for a patch drafted in this session. */
  readonly id: number;
  rings: L.LatLng[][];
}

/** Where one corner of the shape being edited also lives. */
export interface VertexLink {
  readonly shape: number;
  readonly ring: number;
  readonly index: number;
}

/** Key for a corner of the primary shape. */
export function vertexKey(ring: number, index: number): string {
  return `${ring}:${index}`;
}

/**
 * For every corner of the primary shape, the corners elsewhere that sit on the
 * same spot.
 *
 * Rebuilt whenever the ring's indices change - inserting a corner shifts every
 * index after it, and a link list addressing the old positions would drag the
 * wrong corners.
 */
export function linkSharedVertices(
  shapes: readonly EditableShape[],
  primary: number,
): Map<string, VertexLink[]> {
  const links = new Map<string, VertexLink[]>();
  const own = shapes[primary];
  if (!own) return links;

  own.rings.forEach((ring, r) => {
    ring.forEach((point, i) => {
      const found: VertexLink[] = [];
      shapes.forEach((other, s) => {
        if (s === primary) return;
        other.rings.forEach((otherRing, or) => {
          otherRing.forEach((otherPoint, oi) => {
            if (samePoint(point, otherPoint)) {
              found.push({ shape: s, ring: or, index: oi });
            }
          });
        });
      });
      if (found.length) links.set(vertexKey(r, i), found);
    });
  });

  return links;
}

/**
 * Neighbours whose boundary runs along the same segment, and where a corner
 * would have to go in each of them to keep the border one line.
 *
 * A ring is a cycle, so the two corners can appear in either order in the
 * neighbour - it walks its outline the opposite way round, which is the normal
 * case for two patches on opposite sides of a border. Inserting after the
 * earlier of the two positions is right either way; the returned index is where
 * the new corner lands.
 */
export function sharedSegmentInsertions(
  shapes: readonly EditableShape[],
  primary: number,
  a: L.LatLng,
  b: L.LatLng,
): VertexLink[] {
  const insertions: VertexLink[] = [];

  shapes.forEach((other, s) => {
    if (s === primary) return;
    other.rings.forEach((ring, r) => {
      for (let i = 0; i < ring.length; i++) {
        const next = (i + 1) % ring.length;
        const forward = samePoint(ring[i], a) && samePoint(ring[next], b);
        const backward = samePoint(ring[i], b) && samePoint(ring[next], a);
        if (forward || backward) {
          insertions.push({ shape: s, ring: r, index: next });
          return;
        }
      }
    });
  });

  return insertions;
}

/** True when the shape's rings differ from the snapshot they started as. */
export function ringsDiffer(
  now: readonly (readonly L.LatLng[])[],
  before: readonly (readonly L.LatLng[])[],
): boolean {
  if (now.length !== before.length) return true;
  return now.some((ring, r) => {
    const was = before[r];
    return ring.length !== was.length || ring.some((p, i) => !samePoint(p, was[i]));
  });
}

export function cloneRings(rings: readonly (readonly L.LatLng[])[]): L.LatLng[][] {
  return rings.map(ring => ring.map(p => L.latLng(p.lat, p.lng)));
}
